import { FireConfig, IncomeStream, Liability, ManualAsset } from '../types';

export interface FireMetricProfile {
  currentExpenses: number;
  retirementExpenses: number;
  swr: number;
}

export interface FireMetrics {
  fireNumberRetirement: number;
  fireNumberToday: number;
  fiProgressRetirement: number;
  fiProgressToday: number;
  yearsToTarget: number | null;
}

export interface FireWealthBreakdown {
  totalNetWorthToday: number;
  totalAssetsToday: number;
  totalLiabilitiesToday: number;
  liquidFireAssetsToday: number;
  liquidRiskAssetsToday: number;
  illiquidAssetsToday: number;
  pensionAssetsToday: number;
  propertyAssetsToday: number;
}

export interface FireTargets {
  retirementExpensesToday: number;
  projectedRetirementExpensesAtRetirementAge: number;
  fireNumberToday: number;
  fireNumberAtRetirement: number;
}

export interface RetirementSnapshotBreakdown {
  projectedLiquidFireAssetsAtRetirement: number;
  projectedPensionAssetsAtRetirement: number;
  projectedPropertyAssetsAtRetirement: number;
  projectedOtherIlliquidAssetsAtRetirement: number;
  projectedLiabilitiesAtRetirement: number;
  projectedTotalNetWorthAtRetirement: number;
}

export interface RetirementReadinessInput {
  projectedTotalNetWorthAtRetirement: number;
  projectedLiquidFireAssetsAtRetirement: number;
  projectedRetirementExpensesAtRetirement: number;
  fireNumberAtRetirement: number;
  secureIncomeAtRetirement: number;
  withdrawalRate: number;
}

export interface RetirementReadiness extends RetirementReadinessInput {
  safeWithdrawalIncomeAtRetirement: number;
  totalRetirementFundingIncome: number;
  incomeCoveragePct: number;
  liquidFireProgressAtRetirementPct: number;
  totalNetWorthProgressAtRetirementPct: number;
  isFireReady: boolean;
  status: 'On Track' | 'Partial' | 'Off Track';
  message: string;
}

export interface DividendProjectionResult {
  projectedDividends: number;
  safeDividendYield: number;
  rawYield: number;
  wasLimited: boolean;
  warning?: string;
}

export const MIN_PORTFOLIO_FOR_YIELD = 1_000;
export const MAX_REASONABLE_DIVIDEND_YIELD = 0.12;

export const LIQUID_FIRE_ASSET_TYPES: ManualAsset['type'][] = ['Cash', 'ISA', 'GIA'];
export const RISK_LIQUID_ASSET_TYPES: ManualAsset['type'][] = ['Crypto'];
export const PENSION_ASSET_TYPES: ManualAsset['type'][] = ['Pension'];
export const PROPERTY_ASSET_TYPES: ManualAsset['type'][] = ['Real Estate'];

export const safeDivide = (numerator: number, denominator: number) =>
  denominator > 0 ? numerator / denominator : 0;

const sumAssetsByType = (assets: ManualAsset[], types: ManualAsset['type'][]): number =>
  assets.filter((asset) => types.includes(asset.type)).reduce((sum, asset) => sum + (asset.value || 0), 0);

export const calculateFutureValue = (value: number, annualRatePct: number, years: number): number =>
  (value || 0) * Math.pow(1 + ((annualRatePct || 0) / 100), Math.max(0, years));

export const computeFireWealthBreakdown = (
  config: Pick<FireConfig, 'manualAssets' | 'liabilities'>,
  portfolioValue: number
): FireWealthBreakdown => {
  const manualAssets = config.manualAssets || [];
  const liabilities = config.liabilities || [];

  const propertyAssetsToday = sumAssetsByType(manualAssets, ['Real Estate']);
  const pensionAssetsToday = sumAssetsByType(manualAssets, ['Pension']);
  const liquidManualAssetsToday = sumAssetsByType(manualAssets, LIQUID_FIRE_ASSET_TYPES);
  const liquidRiskAssetsToday = sumAssetsByType(manualAssets, RISK_LIQUID_ASSET_TYPES);
  const totalManualAssetsToday = manualAssets.reduce((sum, asset) => sum + (asset.value || 0), 0);
  const totalLiabilitiesToday = liabilities.reduce((sum, liability) => sum + (liability.balance || 0), 0);
  const totalAssetsToday = (portfolioValue || 0) + totalManualAssetsToday;
  const liquidFireAssetsToday = (portfolioValue || 0) + liquidManualAssetsToday + liquidRiskAssetsToday;

  return {
    totalNetWorthToday: totalAssetsToday - totalLiabilitiesToday,
    totalAssetsToday,
    totalLiabilitiesToday,
    liquidFireAssetsToday,
    liquidRiskAssetsToday,
    illiquidAssetsToday: Math.max(0, totalManualAssetsToday - liquidManualAssetsToday - liquidRiskAssetsToday),
    pensionAssetsToday,
    propertyAssetsToday,
  };
};

export const computeFireTargets = (
  config: Pick<FireConfig, 'annualExpenses' | 'retirementExpenses' | 'withdrawalRate' | 'inflationRate' | 'currentAge' | 'retirementAge'>,
  retirementAgeOverride?: number
): FireTargets => {
  const retirementExpensesToday = config.retirementExpenses || config.annualExpenses;
  const withdrawalRate = config.withdrawalRate / 100;
  const yearsToRetirement = Math.max(0, (retirementAgeOverride ?? config.retirementAge) - config.currentAge);
  const projectedRetirementExpensesAtRetirementAge = calculateFutureValue(
    retirementExpensesToday,
    config.inflationRate,
    yearsToRetirement
  );

  return {
    retirementExpensesToday,
    projectedRetirementExpensesAtRetirementAge,
    fireNumberToday: safeDivide(retirementExpensesToday, withdrawalRate),
    fireNumberAtRetirement: safeDivide(projectedRetirementExpensesAtRetirementAge, withdrawalRate),
  };
};

export const projectManualAssetValueAtAge = (
  assets: ManualAsset[],
  typeFilter: ManualAsset['type'][],
  years: number
): number => assets
  .filter((asset) => typeFilter.includes(asset.type))
  .reduce((sum, asset) => sum + calculateFutureValue(asset.value, asset.growthRate, years), 0);

export const projectLiabilityBalanceAtAge = (
  liability: Pick<Liability, 'balance' | 'interestRate' | 'monthlyPayment'>,
  years: number
): number => {
  let balance = Math.max(0, liability.balance || 0);
  const wholeYears = Math.max(0, Math.floor(years));

  for (let year = 0; year < wholeYears; year += 1) {
    if (balance <= 0) return 0;
    const annualPayment = Math.max(0, liability.monthlyPayment || 0) * 12;
    const annualInterest = balance * ((liability.interestRate || 0) / 100);
    balance = Math.max(0, balance - annualPayment + annualInterest);
  }

  return balance;
};

export const projectLiabilityBalancesAtAge = (
  liabilities: Pick<Liability, 'balance' | 'interestRate' | 'monthlyPayment'>[],
  years: number
): number => liabilities.reduce((sum, liability) => sum + projectLiabilityBalanceAtAge(liability, years), 0);

export const computeRetirementSnapshotBreakdown = ({
  projectedLiquidFireAssetsAtRetirement,
  manualAssets,
  liabilities,
  yearsToRetirement,
}: {
  projectedLiquidFireAssetsAtRetirement: number;
  manualAssets: ManualAsset[];
  liabilities: Pick<Liability, 'balance' | 'interestRate' | 'monthlyPayment'>[];
  yearsToRetirement: number;
}): RetirementSnapshotBreakdown => {
  const projectedPensionAssetsAtRetirement = projectManualAssetValueAtAge(manualAssets, PENSION_ASSET_TYPES, yearsToRetirement);
  const projectedPropertyAssetsAtRetirement = projectManualAssetValueAtAge(manualAssets, PROPERTY_ASSET_TYPES, yearsToRetirement);
  const nonOtherTypes = [
    ...LIQUID_FIRE_ASSET_TYPES,
    ...RISK_LIQUID_ASSET_TYPES,
    ...PENSION_ASSET_TYPES,
    ...PROPERTY_ASSET_TYPES,
  ];
  const projectedOtherIlliquidAssetsAtRetirement = manualAssets
    .filter((asset) => !nonOtherTypes.includes(asset.type))
    .reduce((sum, asset) => sum + calculateFutureValue(asset.value, asset.growthRate, yearsToRetirement), 0);
  const projectedLiabilitiesAtRetirement = projectLiabilityBalancesAtAge(liabilities, yearsToRetirement);
  const projectedTotalNetWorthAtRetirement =
    Math.max(0, projectedLiquidFireAssetsAtRetirement) +
    projectedPensionAssetsAtRetirement +
    projectedPropertyAssetsAtRetirement +
    projectedOtherIlliquidAssetsAtRetirement -
    projectedLiabilitiesAtRetirement;

  return {
    projectedLiquidFireAssetsAtRetirement: Math.max(0, projectedLiquidFireAssetsAtRetirement),
    projectedPensionAssetsAtRetirement,
    projectedPropertyAssetsAtRetirement,
    projectedOtherIlliquidAssetsAtRetirement,
    projectedLiabilitiesAtRetirement,
    projectedTotalNetWorthAtRetirement,
  };
};

export const computeIncomeStreamAmountAtAge = (
  stream: IncomeStream,
  age: number,
  currentAge: number,
  incomeGrowthRate: number
): number => {
  const amountMode = stream.amountMode ?? 'todayMoney';
  const growthYears = amountMode === 'nominalAtStartAge'
    ? Math.max(0, age - stream.startAge)
    : Math.max(0, age - currentAge);

  return calculateFutureValue(stream.amount, incomeGrowthRate, growthYears);
};

export const computeSecureIncomeAtAge = (
  streams: IncomeStream[],
  age: number,
  currentAge: number,
  incomeGrowthRate: number
): number => streams
  .filter((stream) => age >= stream.startAge && age < stream.endAge)
  .filter((stream) => ['Pension', 'Annuity'].includes(stream.type))
  .reduce((sum, stream) => sum + computeIncomeStreamAmountAtAge(stream, age, currentAge, incomeGrowthRate), 0);

export const computeDividendProjection = ({
  portfolioValue,
  passiveIncome,
  projectedPortfolioBalance,
  includeDividends = true,
  includeWithdrawals = true,
  safeWithdrawalLimit = 0,
}: {
  portfolioValue: number;
  passiveIncome: number;
  projectedPortfolioBalance: number;
  includeDividends?: boolean;
  includeWithdrawals?: boolean;
  safeWithdrawalLimit?: number;
}): DividendProjectionResult => {
  if (!includeDividends || passiveIncome <= 0) {
    return { projectedDividends: 0, safeDividendYield: 0, rawYield: 0, wasLimited: false };
  }

  const rawYield = portfolioValue >= MIN_PORTFOLIO_FOR_YIELD ? passiveIncome / portfolioValue : 0;
  const finiteRawYield = Number.isFinite(rawYield) ? rawYield : 0;
  const safeDividendYield = Math.min(Math.max(finiteRawYield, 0), MAX_REASONABLE_DIVIDEND_YIELD);
  let projectedDividends = Math.max(0, projectedPortfolioBalance) * safeDividendYield;
  let wasLimited = portfolioValue < MIN_PORTFOLIO_FOR_YIELD || finiteRawYield !== safeDividendYield;

  if (includeWithdrawals && safeWithdrawalLimit > 0 && projectedDividends > safeWithdrawalLimit) {
    projectedDividends = safeWithdrawalLimit;
    wasLimited = true;
  }

  return {
    projectedDividends,
    safeDividendYield,
    rawYield: finiteRawYield,
    wasLimited,
    warning: wasLimited
      ? 'Dividend projection limited because current portfolio value/yield appears unreliable.'
      : undefined,
  };
};

export const computeRetirementReadiness = (input: RetirementReadinessInput): RetirementReadiness => {
  const safeWithdrawalIncomeAtRetirement = Math.max(0, input.projectedLiquidFireAssetsAtRetirement) * (input.withdrawalRate / 100);
  const totalRetirementFundingIncome = input.secureIncomeAtRetirement + safeWithdrawalIncomeAtRetirement;
  const incomeCoveragePct = safeDivide(totalRetirementFundingIncome, input.projectedRetirementExpensesAtRetirement);
  const liquidFireProgressAtRetirementPct = safeDivide(
    input.projectedLiquidFireAssetsAtRetirement,
    input.fireNumberAtRetirement
  );
  const totalNetWorthProgressAtRetirementPct = safeDivide(
    input.projectedTotalNetWorthAtRetirement,
    input.fireNumberAtRetirement
  );
  const isFireReady = incomeCoveragePct >= 1;
  const hasMaterialIlliquidWealth = totalNetWorthProgressAtRetirementPct >= 1 && liquidFireProgressAtRetirementPct < 1;
  const status: RetirementReadiness['status'] = isFireReady ? 'On Track' : hasMaterialIlliquidWealth ? 'Partial' : 'Off Track';

  return {
    ...input,
    safeWithdrawalIncomeAtRetirement,
    totalRetirementFundingIncome,
    incomeCoveragePct,
    liquidFireProgressAtRetirementPct,
    totalNetWorthProgressAtRetirementPct,
    isFireReady,
    status,
    message: isFireReady
      ? 'On Track: projected liquid assets and secure income cover retirement expenses.'
      : hasMaterialIlliquidWealth
        ? 'Partial: pension/property wealth exists, but liquid retirement funding is below target.'
        : 'Off Track: total net worth grows, but liquid FIRE assets and retirement income do not yet cover planned expenses.',
  };
};

export const computeRequiredMonthlySavings = (
  gapAtRetirement: number,
  yearsToRetirement: number,
  annualReturnRatePct: number
): number => {
  if (gapAtRetirement <= 0) return 0;

  const months = Math.max(1, yearsToRetirement * 12);
  const monthlyRate = (annualReturnRatePct / 100) / 12;
  if (monthlyRate === 0) return gapAtRetirement / months;

  return (gapAtRetirement * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
};

export const computeFireMetrics = (
  profile: FireMetricProfile,
  netWorthSnapshot: { currentNetWorth: number; yearsToTarget: number | null }
): FireMetrics => {
  const swr = profile.swr;
  const fireNumberRetirement = safeDivide(profile.retirementExpenses, swr);
  const fireNumberToday = safeDivide(profile.currentExpenses, swr);

  return {
    fireNumberRetirement,
    fireNumberToday,
    fiProgressRetirement: safeDivide(netWorthSnapshot.currentNetWorth, fireNumberRetirement),
    fiProgressToday: safeDivide(netWorthSnapshot.currentNetWorth, fireNumberToday),
    yearsToTarget: netWorthSnapshot.yearsToTarget,
  };
};
